/**
 * DynamicsAIEditor
 *
 * Workflow node editor for the "Dynamics 365 AI" action.
 *
 * Architecture (three required pieces + optional orchestration):
 *   ① AI Config         — which LLM + the system prompt (tells the AI what it can do in D365)
 *   ② Dynamics Config   — which D365 org credentials to use (set up in Integrations → Dynamics)
 *   ③ MCP Servers       — the actual Dynamics API tools (dynamics365_search_contact, etc.)
 *   ④ Scenario Prompts  — copy/paste snippets for the AI Config's system prompt
 *   ⑤ Specialists       — optional: other AI Configs this AI can delegate sub-topics to
 *
 * The Dynamics AI node is NOT the MCP server. The MCP server is what gives the AI tools to
 * call against D365 OData. This node orchestrates the conversation; the MCP server provides
 * the CRM data bridge.
 *
 * This follows the same 5-step pattern as NetsuiteAIEditor.
 */
import * as React from "react";
import { useState } from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useDynamicsConfigs from "@/hooks/useDynamicsConfigs";
import useMcpServers from "@/hooks/useMcpServers";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

// AI providers that support MCP tool calling
const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

/**
 * Dynamics 365 scenario prompts — copy/paste these into your AI Config's system prompt
 * to teach the AI how to handle specific CRM scenarios.
 *
 * Best practice: start with one scenario, test it, then add more. Each snippet is
 * intentionally written as behavioral guidance (not rigid steps) so the AI can adapt
 * to context — e.g. if the visitor already gave their email, the AI won't ask for it again.
 */
const DYNAMICS_SCENARIOS = [
  {
    id: "contact_lookup",
    label: "Contact Lookup",
    description: "Visitor wants to verify their info or you need to identify who they are.",
    prompt: `When the visitor mentions their name or you need to identify them, use dynamics365_search_contact to find their record by email or full name. If a match is found, confirm their name and role — do not read out sensitive fields (phone, address) unless the visitor asks. If no contact is found, offer to create one or escalate to an agent. Always ask for email first if not already provided, as it is the most reliable search key.`
  },
  {
    id: "lead_qualification",
    label: "Lead Qualification",
    description: "Collect visitor details and create a qualified CRM lead from the conversation.",
    prompt: `Your goal is to qualify this visitor as a sales lead. Collect the following naturally during the conversation — do not list them all at once: first name, last name, business email, company name, phone number, and the main topic or need they have. Once you have all required details, call dynamics365_create_lead to create the record. Then call dynamics365_push_note to attach a concise conversation summary to the lead. Confirm to the visitor that your team will follow up within 1 business day.`
  },
  {
    id: "support_intake",
    label: "Support Case Intake",
    description: "Visitor has a support issue — look them up, log the issue, escalate if needed.",
    prompt: `When the visitor describes a problem or support request, first call dynamics365_search_contact using their email to find their record. If found, acknowledge who they are. Ask them to describe their issue briefly and rate its urgency (low / medium / high). Call dynamics365_push_note to log the issue details as a note on their contact record (format: "Support Request: [issue summary] | Urgency: [level] | Chat date: [today's date]"). Inform the visitor that a ticket has been logged and they will receive follow-up by email. If the issue is urgent or you cannot resolve it, escalate to a human agent.`
  },
  {
    id: "account_research",
    label: "Account Research",
    description: "Look up a company's CRM record before a sales call or for account context.",
    prompt: `When asked about a company or account, call dynamics365_search_account with the company name. Share relevant non-sensitive details: industry, website, phone, and employee count. If the account is not found, offer to create a new account record and collect the required details. Use dynamics365_push_note to log the research conversation as a note on the account. Never share financial data (revenue, credit terms) unless the requester is authenticated as an internal team member.`
  },
  {
    id: "conversation_summary",
    label: "Push Conversation Summary to CRM",
    description: "At conversation end, attach a summary note to the contact or account record.",
    prompt: `At the end of every conversation, attempt to identify the contact using dynamics365_search_contact with their email address. If found, call dynamics365_push_note with a concise summary (150 words max) including: the visitor's main question or request, the outcome, and any follow-up items. Format the note title as "Velaro Chat — [date]". If the contact is not found, search for their account instead and attach the note there. Confirm to the visitor that the conversation has been logged in your CRM.`
  }
];

export default function DynamicsAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: dynamicsConfigs, loading: dynLoading } = useDynamicsConfigs();
  const { items: mcpServers } = useMcpServers();
  const [copiedScenario, setCopiedScenario] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const dynOptions = dynamicsConfigs
    .filter((d) => d.enabled)
    .map((d) => ({
      value: d.id,
      label: d.displayName || d.orgName || `Config #${d.id}`
    }));
  const allDynOptions = dynamicsConfigs.map((d) => ({
    value: d.id,
    label:
      (d.displayName || d.orgName || `Config #${d.id}`) +
      (d.enabled ? "" : " (disabled)")
  }));

  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];

  // Collect all enabled tool names for the context inspector
  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  async function copyScenarioPrompt(id: string, prompt: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedScenario(id);
      setTimeout(() => setCopiedScenario(null), 2000);
    } catch {
      // clipboard unavailable — graceful fallback (no crash)
    }
  }

  return (
    <>
      {/* Setup guide — collapsible how-it-works card */}
      <AISetupGuide
        dataSourceName="Dynamics 365"
        dataSourcePath="/integrations/dynamics"
        hasAiConfig={!!selectedAiConfig}
        hasMcpServers={enabledMcpServers.length > 0}
        hasDataSourceConfig={!!node.dynamicsConfigId}
      />

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found.{" "}
            <a href="/bots" className="underline font-medium">
              Create an AI Config first →
            </a>
            <p className="mt-1 text-slate-500">
              The AI Config holds the system prompt — it tells the AI which Dynamics 365 operations
              it can perform and how to handle CRM conversations.
            </p>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            This AI config has no system prompt.{" "}
            <a href="/bots" className="underline">
              Add a prompt →
            </a>{" "}
            telling it what it can do in Dynamics 365. Use the Scenario Prompts below to get
            started.
          </p>
        )}
      </FormGroup>

      {/* ── Step 2: Dynamics 365 Config ── */}
      <FormGroup label="Dynamics 365 Org">
        {!dynLoading && dynamicsConfigs.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No Dynamics 365 org connected.{" "}
            <a href="/integrations/dynamics" className="underline font-medium">
              Connect a Dynamics 365 org →
            </a>
            <p className="mt-1 text-slate-500">
              The Dynamics config holds the OAuth credentials (Entra App Registration) needed to
              query and write records in your D365 environment.
            </p>
          </div>
        ) : (
          <>
            <Select
              options={[
                { value: 0, label: "— Select Dynamics 365 Org —" },
                ...(dynOptions.length > 0 ? dynOptions : allDynOptions)
              ]}
              value={node.dynamicsConfigId ?? 0}
              onChange={(value) => onUpdate({ dynamicsConfigId: Number(value) || undefined })}
            />
            {dynamicsConfigs.length > 0 && dynOptions.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                All connected orgs are disabled.{" "}
                <a href="/integrations/dynamics" className="underline">
                  Enable an org →
                </a>
              </p>
            )}
          </>
        )}
      </FormGroup>

      {/* ── Step 3: MCP Tool Servers ── */}
      <FormGroup label="MCP Tool Servers">
        {enabledMcpServers.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            No MCP tool servers configured.{" "}
            <a href="/integrations/mcp" className="text-blue-600 underline">
              Add an MCP server →
            </a>
            <p className="mt-1">
              Without tools, the AI can only chat — it can't actually query or write Dynamics 365
              data. You need a Dynamics 365 MCP server that exposes tools like{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">
                dynamics365_search_contact
              </code>
              ,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">
                dynamics365_create_lead
              </code>
              , etc.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked
                      ? "border-blue-300 bg-blue-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                      <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                    </div>
                    {server.description && (
                      <span className="text-xs text-slate-500">{server.description}</span>
                    )}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono"
                          >
                            {t}
                          </span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">
                            +{server.toolNames.length - 6} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </FormGroup>

      {/* ── Scenario Prompt Helper ── */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Scenario Prompts
          </span>
          <span className="text-xs text-slate-400 italic">for your AI Config</span>
        </div>
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Copy these snippets into your AI Config's system prompt to tell the AI how to handle
          specific Dynamics 365 scenarios. These are behavioral guidelines — the AI will adapt
          based on what the visitor already told it, so it won't ask for info it already has.
        </p>
        <div className="flex flex-col gap-2">
          {DYNAMICS_SCENARIOS.map((scenario) => {
            const isExpanded = expandedScenario === scenario.id;
            const isCopied = copiedScenario === scenario.id;
            return (
              <div
                key={scenario.id}
                className="border border-slate-200 rounded bg-white overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <span className="text-xs font-medium text-slate-800">{scenario.label}</span>
                    {!isExpanded && (
                      <p className="text-xs text-slate-400 mt-0.5">{scenario.description}</p>
                    )}
                  </div>
                  <span className="text-slate-400 text-xs ml-2 shrink-0">
                    {isExpanded ? "▲" : "▼"}
                  </span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{scenario.description}</p>
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2.5 whitespace-pre-wrap leading-relaxed font-mono mb-2">
                      {scenario.prompt}
                    </pre>
                    <button
                      type="button"
                      onClick={() => copyScenarioPrompt(scenario.id, scenario.prompt)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        isCopied
                          ? "border-green-300 bg-green-50 text-green-700"
                          : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isCopied ? "✓ Copied!" : "Copy prompt snippet"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">
                      Paste into your AI Config's system prompt, then customize for your business.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Context Inspector (debug) ── */}
      <AIContextInspector mcpToolNames={enabledToolNames} />

      {/* ── Step 4: Specialist Agents (orchestration) ── */}
      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection
          node={node}
          onUpdate={onUpdate}
          aiConfigurations={aiConfigurations}
          enabledMcpServers={enabledMcpServers}
        />
      </div>

      {/* ── Jump to Workflow Section ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
